import React, { useEffect, useRef } from "react";
import { Chart } from "chart.js/auto";

export function BarCharts({ results = {} }) {
  const {
    presentValue: initialValue,
    upfrontRental: rentalIncome,
    ownerProfit: profit,
    totalLoan: loanAmount,
    loanInterest: interest,
    loan: currentOpenLoan,
    investorProfit: profitShare,
    rentalPeriod,
  } = results;

  const ownerChartRef = useRef(null);
  const investorChartRef = useRef(null);
  const ownerChartInstance = useRef(null);
  const investorChartInstance = useRef(null);

  useEffect(() => {
    // Destroy previous instance if it exists
    if (ownerChartInstance.current) {
      ownerChartInstance.current.destroy();
    }

    ownerChartInstance.current = new Chart(ownerChartRef.current, {
      type: "pie",
      data: {
        labels: [
          "Initial Property Value",
          "Upfront Rental Income",
          "Extra Proceed",
        ],
        datasets: [
          {
            data: [initialValue, rentalIncome, profit],
            backgroundColor: ["#9e9e9e", "#ff7a5b", "#10b981"],
            borderColor: ["#2e2e2e", "#df6a4b", "#059669"],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.raw;
                return `${label}: $${Number(value).toLocaleString()}`;
              },
            },
          },
        },
      },
    });
  }, [initialValue, rentalIncome, profit]);

  useEffect(() => {
    if (investorChartInstance.current) {
      investorChartInstance.current.destroy();
    }

    investorChartInstance.current = new Chart(investorChartRef.current, {
      type: "pie",
      data: {
        labels: ["Loan Repayment", "Interest", "Extra Proceed"],
        datasets: [
          {
            data: [loanAmount, interest, profitShare],
            backgroundColor: ["#93c5fd", "#8b5cf6", "#10b981"],
            borderColor: ["#60a5fa", "#7c3aed", "#059669"],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.raw;
                return `${label}: $${Number(value).toLocaleString()}`;
              },
            },
          },
        },
      },
    });
  }, [loanAmount, interest, profitShare]);

  return (
    <div>
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Owner's Financial Outcome
        </h3>
        <div className="chart-container">
          <div className="h-72">
            <canvas ref={ownerChartRef}></canvas>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <p className="text-gray-600">Initial Property Value:</p>
            <p id="owner-initial-value" className="text-lg font-medium">
              ${initialValue.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Upfront Rental Income:</p>
            <p
              id="owner-rental-income"
              className="text-lg font-medium text-blue-600"
            >
              ${rentalIncome.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Extra Proceed:</p>
            <p
              id="owner-profit-value"
              className="text-lg font-medium text-green-600"
            >
              ${profit.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">
              Total Cash In Hand:{" "}
              <span className="text-gray-500 text-sm">
                (Total Owners Payoff - Current Open Loan)
              </span>
            </p>
            <p
              id="owner-total-return"
              className="text-lg font-medium text-blue-600"
            >
              ${(initialValue + rentalIncome + profit).toLocaleString()} - $
              {currentOpenLoan.toLocaleString()} = $
              {(
                initialValue +
                rentalIncome +
                profit -
                currentOpenLoan
              ).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Return on Initial Value:</p>
            <p id="owner-roi" className="text-lg font-medium text-purple-600">
              {(((rentalIncome + profit) / initialValue) * 100).toFixed(2)}%
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Investor's Financial Outcome
        </h3>
        <div className="chart-container">
          <div className="h-72">
            <canvas ref={investorChartRef}></canvas>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <p className="text-gray-600">Construction + Upfront Rental Loan:</p>
            <p id="investor-loan-amount" className="text-lg font-medium">
              ${loanAmount.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Interest Earned:</p>
            <p
              id="investor-interest"
              className="text-lg font-medium text-green-600"
            >
              ${interest.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Extra Proceed:</p>
            <p
              id="investor-profit-share"
              className="text-lg font-medium text-blue-600"
            >
              ${profitShare.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Total Return:</p>
            <p
              id="investor-total-return"
              className="text-lg font-medium text-purple-600"
            >
              ${(loanAmount + interest + profitShare).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Return on Investment:</p>
            <p
              id="investor-roi"
              className="text-lg font-medium text-purple-600"
            >
              {(
                ((interest + profitShare) /
                  (loanAmount * (rentalPeriod / 12))) *
                100
              ).toFixed(2)}
              % Annually
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BarCharts;
